import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, RefreshCw } from 'lucide-react';
import { uploadAPI } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  accept = ".jpg,.jpeg,.png,.gif,.webp",
  maxSize = 10 // MB
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [retryFile, setRetryFile] = useState(null);
  const inputRef = useRef(null);

  // Get full URL for display
  const getDisplayUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
    return url;
  };

  const handleFile = async (file, isRetry = false) => {
    setError('');
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File too large. Maximum size is ${maxSize}MB.`);
      return;
    }

    setUploading(true);
    setRetryFile(file);
    
    try {
      const result = await uploadAPI.upload(file);
      onChange(result.url);
      setRetryFile(null);
      setError('');
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload image.';
      
      if (err.message?.includes('Network Error') || err.code === 'ECONNABORTED') {
        setError('Connection error. Please check your internet and try again.');
      } else if (err.response?.status === 500) {
        setError('Server is busy. Please wait a moment and try again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = () => {
    if (retryFile) {
      handleFile(retryFile, true);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayUrl = getDisplayUrl(value);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {displayUrl ? (
        // Image Preview
        <div className="relative group">
          <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
            <img
              src={displayUrl}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="px-3 py-2 bg-white text-gray-900 text-sm rounded-md hover:bg-gray-100 transition-colors"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1 truncate">
            {value.startsWith('/api/uploads/') ? 'Uploaded image' : value}
          </p>
        </div>
      ) : (
        // Upload Area
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${dragActive ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'}
            ${uploading ? 'pointer-events-none opacity-70' : ''}
          `}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-gray-400 animate-spin mb-3" />
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Upload className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium text-black">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-400">
                JPG, PNG, GIF or WebP (max {maxSize}MB)
              </p>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />

      {error && (
        <div className="flex items-center gap-2 mt-2">
          <p className="text-sm text-red-600 flex-1">{error}</p>
          {retryFile && (
            <button
              type="button"
              onClick={handleRetry}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>
      )}

      {/* Optional: URL input fallback */}
      <div className="mt-3">
        <details className="text-sm">
          <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
            Or paste image URL
          </summary>
          <input
            type="url"
            value={value?.startsWith('/api/uploads/') ? '' : value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          />
        </details>
      </div>
    </div>
  );
};

export default ImageUpload;
